import { createContext, useContext, type ReactNode } from "react";

/**
 * Sprint 24.1 Hotfix PR-H4 — minimal navigation context.
 *
 * The Admin Console doesn't use react-router (Sprint 6 decision —
 * the surface was small enough that a state switch in `AdminApp.tsx`
 * was net-positive for bundle size). That worked fine until Sprint 24
 * grew the page count and operator findings #4 + #5 surfaced the
 * need for cross-page CTAs (e.g. Members → Invitations).
 *
 * Rather than reach for a router, we expose a tiny `useAdminNav()`
 * hook that pages can call to navigate to a sibling page. AdminApp
 * is the single provider and is the only place that knows the closed
 * `AdminPage` enum.
 *
 * Sprint 25 Wave C PR-C4 — extended with a per-navigation `params`
 * payload so the Customer Launch Dashboard can drill into a specific
 * tenant on Tenant 360° without round-tripping through the picker.
 * Consumers read the most recent params via `useAdminNavParams()`;
 * the params are reset when `navigateTo` is called without a `params`
 * argument so a stale tenant id can't bleed across page changes.
 */

export interface AdminNavParams {
  readonly tenantId?: string;
  /**
   * Sprint 32 (GT-TENANT-EDIT-01) — when true, the receiving page
   * may auto-open its edit affordance. Today only Tenant 360°
   * reads this (`apps/admin/src/tenants/TenantDetailPage.tsx`)
   * so the Tenants list "Edit" row action lands directly in edit
   * mode instead of forcing the operator to click again. Reset on
   * any paramless `navigateTo` call (same as `tenantId`).
   */
  readonly edit?: boolean;
  /**
   * Sprint 54.2 (Admin Console Recovery — Batch 2) — optional
   * scroll-target hint for pages that compose multiple sections.
   * Today only `PeoplePage` reads this — when set to `"members"`
   * or `"invitations"`, the page scrolls the corresponding
   * section into view on mount. Reset on every paramless
   * `navigateTo` call so a stale hint never persists.
   */
  readonly scrollTo?: "members" | "invitations";
}

export interface AdminNavValue {
  /**
   * Navigate to a sibling admin page. The argument is a page name
   * from the closed `AdminPage` union defined in AdminApp.tsx;
   * unknown values are ignored by the provider with a console
   * warning so a typo never crashes the app.
   *
   * Optional `params` payload is the recipient page's contract —
   * each consuming page reads only the keys it understands. Pass
   * an empty object (or omit) when no params are needed; the
   * provider clears any prior params on a paramless navigation
   * so a stale tenant id from a previous click can't bleed across
   * pages.
   */
  readonly navigateTo: (page: string, params?: AdminNavParams) => void;
  /**
   * Most recently passed navigation params. Pages that participate
   * in the contract read this through `useAdminNavParams()` to
   * decide what to load.
   */
  readonly params: AdminNavParams;
}

const AdminNavContext = createContext<AdminNavValue | null>(null);

export function AdminNavProvider({
  value,
  children,
}: {
  value: AdminNavValue;
  children: ReactNode;
}) {
  return <AdminNavContext.Provider value={value}>{children}</AdminNavContext.Provider>;
}

export function useAdminNav(): AdminNavValue {
  const ctx = useContext(AdminNavContext);
  if (!ctx) {
    // In production we don't want to crash if a page is rendered
    // outside the provider for some reason; return a no-op so the
    // page still renders. In tests / dev, throw so the misuse is
    // caught.
    if (typeof process !== "undefined" && process.env?.["NODE_ENV"] !== "production") {
      throw new Error("useAdminNav must be used inside AdminNavProvider");
    }
    return { navigateTo: () => undefined, params: {} };
  }
  return ctx;
}

/**
 * Convenience selector for pages that only consume the navigation
 * params (e.g. Tenant 360° reading the tenant id passed by the
 * Customer Launch Dashboard). Returns the same `params` object
 * `useAdminNav()` would return; provided as a shorter import for
 * read-only consumers.
 */
export function useAdminNavParams(): AdminNavParams {
  return useAdminNav().params;
}
